/**
 * LineGRNsModal — the receipts behind one PO line, as a second drill-down.
 *
 * CR-7276 rework. A drill-down row already answered "which line failed"; the
 * question it did not answer was "why", and the answer is the GRNs booked
 * against that line — five receipts spread over a month, most early, one on
 * time. The old platform opened exactly this on a second click and the new
 * one had nothing behind it.
 *
 * Deliberately a nested modal rather than a new browser tab: the user is
 * reading a month's failures and wants the detail for one of them without
 * losing their place in the list they are working through. The blue links
 * inside a row still navigate (PO header, line detail, deliveries) — those
 * are a different intent and are untouched.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, AlertCircle, PackageSearch } from 'lucide-react';
import { purchasingAPI, GoodsReceiptNote } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { getOnTimeStatusStyle } from './drilldownUtils';
import { ORIGINAL_REQUIRED_DATE } from '../../config/dateLabels';

interface LineGRNsModalProps {
  /** PO line whose receipts to show; null closes the modal. */
  poLineId: number | null;
  /** e.g. "PO 4500123 / Line 2" — shown in the header so the row is identifiable. */
  label?: string;
  onClose: () => void;
}

/**
 * Column order: the verdict first, then which receipt it was, then when, then
 * how much. That is the order the question gets asked in — "was it late?",
 * "which GRN?", "when did it land?", "how much came?" — rather than the
 * old platform's, which opened on quantities and buried the verdict off the
 * right-hand edge.
 */
const COLUMNS = [
  'On-time Status', 'GRN Number',
  // 'Delivery Date' here is the GRN receipt date, not a PO-line date concept.
  'MRP Date', 'Review Date', ORIGINAL_REQUIRED_DATE, 'Delivery Date',
  'Qty Ordered', 'Qty Received',
  'Converted Unit Price', 'Total Value', 'Release Note Number',
];

export default function LineGRNsModal({ poLineId, label, onClose }: LineGRNsModalProps) {
  const { formatDate } = useFormatters();
  const { isFeatureEnabled } = useMenuSettings();
  // At PO-line grain the ODD is the date each verdict was measured against
  // (the line's ODD — verdict_anchor_date), not the stored delivery_date a
  // feed can re-plan away from it. Schedule grain keeps the line date it
  // showed before.
  const scheduleGrain = isFeatureEnabled('po_line_schedules');
  const [grns, setGrns] = useState<GoodsReceiptNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGRNs = useCallback(async () => {
    if (poLineId === null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await purchasingAPI.getLineGRNSummary(poLineId);
      setGrns(res.data.grns ?? []);
    } catch (err: unknown) {
      console.error('Error loading GRNs for line:', err);
      setError('Could not load the receipts for this line.');
    } finally {
      setLoading(false);
    }
  }, [poLineId]);

  useEffect(() => { fetchGRNs(); }, [fetchGRNs]);

  const dash = (v: unknown) =>
    v === null || v === undefined || v === '' ? '—' : String(v);
  const date = (v?: string | null) => (v ? formatDate(v) : '—');

  return (
    <Dialog open={poLineId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[95vw] w-[1500px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5" />
            Number of GRNs: {loading ? '…' : grns.length}
          </DialogTitle>
          <DialogDescription>
            {label ? `Receipts booked against ${label}` : 'Receipts booked against this line'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-3" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchGRNs} className="mt-4">Try again</Button>
          </div>
        ) : grns.length === 0 ? (
          // A failed line with no receipts is a real and informative state —
          // nothing was ever delivered — so it is said plainly rather than
          // shown as an empty grid.
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <PackageSearch className="h-8 w-8 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No goods receipts have been booked against this line.
            </p>
          </div>
        ) : (
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b">
                  {COLUMNS.map((c) => (
                    <th key={c} className="text-left font-medium text-muted-foreground px-3 py-2 whitespace-nowrap">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grns.map((g) => (
                  <tr key={g.id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {(() => {
                        // Reuse the drill-down's own styling so Early / On Time
                        // / Late read identically here and in the list above.
                        const raw = g.on_time_status ?? '';
                        if (!raw && !g.on_time_status_display) return '—';
                        const style = getOnTimeStatusStyle(String(raw));
                        return (
                          <Badge className={style.className}>
                            {g.on_time_status_display || style.label}
                          </Badge>
                        );
                      })()}
                    </td>
                    {/* Which receipt this row is, and a way through to it:
                        Deliveries filtered to this GRN (dlv_grn is the page's
                        own GRN filter, so it lands filtered rather than on a
                        free-text search). New tab, so the drill-down you are
                        working through stays open behind it. */}
                    <td className="px-3 py-2 whitespace-nowrap font-medium">
                      {g.grn_number ? (
                        <a
                          href={`/purchasing?tab=delivery&dlv_grn=${encodeURIComponent(g.grn_number)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {g.grn_number}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{date(g.mrp_date)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{date(g.review_date)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {date(scheduleGrain ? g.original_delivery_date : (g.verdict_anchor_date ?? g.original_delivery_date))}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{date(g.receipt_date)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dash(g.quantity_ordered)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dash(g.quantity_received)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dash(g.unit_price_converted ?? g.unit_price)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dash(g.total_value_converted ?? g.total_value)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dash(g.release_note_number)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
